import { useState } from 'react';
import type { Recommendation, RecommendationStatus } from '@lizrd/contracts';
import type { RecommendationPriorityTier } from '@lizrd/contracts';
import { usd } from '../lib/graph-visuals';
import { InlineMarkdown } from './ui/markdown-lite';
import { appliedAlternative } from '../lib/reco-compliance';
import { cn } from '../lib/utils';
import { CategoryIcon } from './CategoryIcon';
import { Pin, User, Sparkles } from 'lucide-react';
import { ProviderChip } from './recommendations/ProviderChip';
import type { LifecycleAction } from './RecommendationLifecycle';

/** The effective priority tier a user sees — their manual override, else the
 * platform-computed tier. */
export const effectiveTier = (rec: Recommendation) => rec.userPriorityTier ?? rec.priorityTier;

/** Jira-style priority glyph: a red up chevron (high), a blue bar (normal), or a
 * green down chevron (low) — a compact, at-a-glance signal that frees the card
 * header for the title + resource. */
const PRIORITY_INDICATOR: Record<
  RecommendationPriorityTier,
  { label: string; color: string; shape: 'up' | 'eq' | 'down' }
> = {
  high: { label: 'High priority', color: 'var(--danger)', shape: 'up' },
  medium: { label: 'Normal priority', color: 'var(--info)', shape: 'eq' },
  low: { label: 'Low priority', color: 'var(--viridian)', shape: 'down' },
};

function PriorityGlyph({ shape }: { shape: 'up' | 'eq' | 'down' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {shape === 'eq' ? (
        <>
          <path d="M5 9h14" />
          <path d="M5 15h14" />
        </>
      ) : shape === 'up' ? (
        <path d="m6 14 6-6 6 6" />
      ) : (
        <path d="m6 10 6 6 6-6" />
      )}
    </svg>
  );
}

/** A compact priority indicator; a pin marks a manual override (PRI-007). */
function PriorityIndicator({ rec }: { rec: Recommendation }) {
  const meta = PRIORITY_INDICATOR[effectiveTier(rec)];
  if (!meta) return null;
  const manual = rec.userPriorityTier != null;
  return (
    <span
      title={`${meta.label}${manual ? ' · set manually' : ''}`}
      aria-label={meta.label}
      className="flex flex-none items-center"
      style={{ color: meta.color }}
    >
      <PriorityGlyph shape={meta.shape} />
      {manual && <Pin aria-hidden className="ml-0.5 h-2.5 w-2.5" />}
    </span>
  );
}

const PAGE_SIZE = 25;

/** The board columns. "In Validation" groups executed items awaiting the outcome. */
const LANES: {
  key: string;
  label: string;
  statuses: RecommendationStatus[];
  accent: string;
}[] = [
  { key: 'open', label: 'Open', statuses: ['open'], accent: 'var(--iris)' },
  { key: 'accepted', label: 'Accepted', statuses: ['accepted'], accent: 'var(--info)' },
  {
    key: 'validating',
    label: 'In Validation',
    statuses: ['completed', 'failed_validation'],
    accent: 'var(--warning)',
  },
  { key: 'validated', label: 'Validated', statuses: ['validated'], accent: 'var(--viridian)' },
  { key: 'rejected', label: 'Rejected', statuses: ['rejected'], accent: 'var(--danger)' },
];

const laneOf = (status: RecommendationStatus): string | null =>
  LANES.find((l) => l.statuses.includes(status))?.key ?? null;

/** The lifecycle action a drag from one lane to another maps to, or null when the
 * move isn't allowed (invalid transition, or a system-only lane like Validated). */
function actionForMove(fromStatus: RecommendationStatus, toLane: string): LifecycleAction | null {
  const from = laneOf(fromStatus);
  if (!from || from === toLane) return null;
  switch (toLane) {
    case 'open':
      return 'reopen';
    case 'accepted':
      return from === 'open' || from === 'rejected' ? 'accept' : null;
    case 'validating':
      return from === 'accepted' ? 'complete' : null;
    case 'rejected':
      return from === 'open' || from === 'accepted' ? 'reject' : null;
    default:
      return null; // 'validated' is a system outcome, never a manual drop
  }
}

/**
 * The Cost Optimization Board — a Kanban of the recommendation lifecycle. Compact,
 * draggable cards flow Open → Accepted → In Validation → Validated (or Rejected);
 * dragging a card to a valid lane fires the matching lifecycle action, and a click
 * opens the full detail/execution modal. New (Open) items stand out; each lane
 * shows up to 25 cards and paginates the rest.
 */
export function CostOptimizationBoard({
  items,
  canManage,
  pendingId,
  onOpen,
  onAction,
  selectMode = false,
  selected,
  onToggleSelect,
}: {
  items: Recommendation[];
  canManage: boolean;
  pendingId: string | null;
  onOpen: (id: string) => void;
  onAction: (id: string, action: LifecycleAction) => void;
  /** Bulk-select mode (ACT-010): when on, cards reveal a checkbox (animated in). */
  selectMode?: boolean;
  selected?: Set<string>;
  onToggleSelect?: (id: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overLane, setOverLane] = useState<string | null>(null);
  const [pages, setPages] = useState<Record<string, number>>({});
  const dragStatus = dragId ? (items.find((i) => i.id === dragId)?.status ?? null) : null;

  return (
    <div className="overflow-x-auto pb-2">
      <div className="grid min-w-[1040px] grid-cols-5 gap-3">
        {LANES.map((lane) => {
          const cards = items.filter((i) => lane.statuses.includes(i.status));
          const pageCount = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));
          const page = Math.min(pages[lane.key] ?? 0, pageCount - 1);
          const visible = cards.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
          const canDrop = canManage && !!dragStatus && actionForMove(dragStatus, lane.key) !== null;
          const active = overLane === lane.key && canDrop;
          return (
            <div
              key={lane.key}
              onDragOver={(e) => {
                if (canDrop) {
                  e.preventDefault();
                  setOverLane(lane.key);
                }
              }}
              onDragLeave={() => setOverLane((l) => (l === lane.key ? null : l))}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain') || dragId;
                const rec = id ? items.find((i) => i.id === id) : null;
                if (rec) {
                  const action = actionForMove(rec.status, lane.key);
                  if (action) onAction(rec.id, action);
                }
                setDragId(null);
                setOverLane(null);
              }}
              className={cn(
                'flex flex-col rounded-lg bg-surface-2/70 transition-colors',
                // A single uniform 2px border when a valid drop is hovering — no
                // ring, so the highlight is the same thickness on every side.
                active ? 'border-2 border-iris' : 'border border-hairline',
              )}
            >
              <div className="flex items-center gap-2 rounded-t-lg border-b border-hairline bg-surface-2 px-3 py-2">
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: lane.accent }}
                />
                <span className="text-xs font-semibold text-bone">{lane.label}</span>
                <span className="ml-auto rounded-full bg-surface px-1.5 text-[0.65rem] text-muted">
                  {cards.length}
                </span>
              </div>
              <div className="flex-1 space-y-2 p-2">
                {cards.length === 0 ? (
                  <p className="px-1 py-8 text-center text-[0.7rem] text-muted">—</p>
                ) : (
                  visible.map((rec) => (
                    <BoardCard
                      key={rec.id}
                      rec={rec}
                      draggable={canManage && pendingId !== rec.id}
                      dragging={dragId === rec.id}
                      // "New" (and the tinted card) shows only until the user first
                      // opens it — an unopened open item, per-user.
                      highlight={lane.key === 'open' && !rec.viewed}
                      pending={pendingId === rec.id}
                      onOpen={onOpen}
                      onDragStart={setDragId}
                      selectMode={canManage && selectMode}
                      selected={selected?.has(rec.id) ?? false}
                      onToggleSelect={onToggleSelect}
                    />
                  ))
                )}
              </div>
              {pageCount > 1 && (
                <div className="flex items-center justify-between gap-2 border-t border-hairline px-2 py-1.5 text-[0.65rem] text-muted">
                  <button
                    disabled={page === 0}
                    onClick={() => setPages((p) => ({ ...p, [lane.key]: page - 1 }))}
                    className="rounded-sm px-1.5 py-0.5 font-semibold hover:text-bone disabled:opacity-40"
                  >
                    ‹ Prev
                  </button>
                  <span>
                    {page + 1}/{pageCount}
                  </span>
                  <button
                    disabled={page >= pageCount - 1}
                    onClick={() => setPages((p) => ({ ...p, [lane.key]: page + 1 }))}
                    className="rounded-sm px-1.5 py-0.5 font-semibold hover:text-bone disabled:opacity-40"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BoardCard({
  rec,
  draggable,
  dragging,
  highlight,
  pending,
  onOpen,
  onDragStart,
  selectMode,
  selected,
  onToggleSelect,
}: {
  rec: Recommendation;
  draggable: boolean;
  dragging: boolean;
  highlight: boolean;
  pending: boolean;
  onOpen: (id: string) => void;
  onDragStart: (id: string | null) => void;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const isReliability = rec.category === 'reliability';
  const isValidated = rec.status === 'validated' && rec.realizedMonthlySaving != null;
  // If the customer accepted the NIS2-compliant alternative, the headline reflects
  // that (smaller) saving rather than the original, more aggressive change.
  const alt = appliedAlternative(rec);
  const savingLabel = isValidated ? 'realized' : 'save';
  const savingValue = isValidated
    ? rec.realizedMonthlySaving!
    : (alt?.saving ?? rec.estimatedMonthlySaving);
  const hint =
    rec.status === 'completed'
      ? { text: '⏳ Validating', color: 'var(--warning)' }
      : rec.status === 'failed_validation'
        ? { text: '⚠ Needs attention', color: 'var(--warning)' }
        : null;

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', rec.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(rec.id);
      }}
      onDragEnd={() => onDragStart(null)}
      onClick={() => onOpen(rec.id)}
      style={
        highlight
          ? { backgroundColor: 'color-mix(in srgb, var(--iris) 16%, var(--surface))' }
          : undefined
      }
      className={cn(
        'rounded-md border p-2.5 text-left shadow-sm transition-colors',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        highlight
          ? 'border-iris/70 hover:brightness-105'
          : 'border-hairline bg-surface hover:bg-surface-2',
        dragging && 'opacity-40',
        pending && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-1.5">
        {/* Bulk-select checkbox — collapses to zero width when not in select mode,
            so it slides + fades in softly when "Apply bulk actions" is pressed. */}
        <div
          className={cn(
            'flex items-start overflow-hidden transition-all duration-200 ease-out',
            selectMode ? 'mr-0.5 w-4 opacity-100' : 'w-0 opacity-0',
          )}
          aria-hidden={!selectMode}
        >
          <input
            type="checkbox"
            checked={selected}
            tabIndex={selectMode ? 0 : -1}
            aria-label={`Select ${rec.title}`}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect?.(rec.id)}
            className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-iris"
          />
        </div>
        <CategoryIcon category={rec.category} className="mt-0.5 text-muted" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-bone">
            <InlineMarkdown text={rec.title} />
          </div>
          <div className="flex items-center gap-1.5">
            <ProviderChip provider={rec.provider} />
            <span className="truncate font-mono text-[0.6rem] text-muted">{rec.resourceName}</span>
          </div>
          {rec.assignedToName && (
            <div className="mt-0.5 flex items-center gap-1 truncate text-[0.6rem] text-haze">
              <User aria-hidden className="h-3 w-3 shrink-0" />
              <span className="truncate">{rec.assignedToName}</span>
            </div>
          )}
        </div>
        <PriorityIndicator rec={rec} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        {highlight ? (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-iris px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wide text-on-iris">
            <Sparkles aria-hidden className="h-2.5 w-2.5" />
            New
          </span>
        ) : hint ? (
          <span className="text-[0.6rem] font-semibold" style={{ color: hint.color }}>
            {hint.text}
          </span>
        ) : (
          <span />
        )}
        {isReliability ? (
          <span className="rounded-full bg-warning/15 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-wide text-warning">
            ⬆ Scale up
          </span>
        ) : (
          <span className="font-display text-xs font-bold text-iris">
            <span className="mr-0.5 text-[0.55rem] font-semibold uppercase tracking-wide text-muted">
              {savingLabel}
            </span>
            {usd(savingValue)}
            <span className="text-[0.55rem] font-normal text-muted">/mo</span>
          </span>
        )}
      </div>
    </div>
  );
}
